import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Shield, Download, ExternalLink, Pencil, X, Save, Image as ImageIcon, FileText, Users,
  CheckCircle2, Ban, Plus, Trash2, Clock, Mail, ArrowLeft, ArrowRight, Lock, LayoutGrid, Search, UserCog, Activity, Bell, Filter, ArrowUpDown, ArrowUp, ArrowDown, Eye, FlaskConical,
} from "lucide-react";
import SiteNav from "@/components/SiteNav";
import Login from "@/pages/Login";
import SiteFooter from "@/components/SiteFooter";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ChangeHistory } from "@/components/ChangeHistory";
import { TrashSection } from "@/components/TrashSection";
import { LoginInfo } from "@/components/LoginInfo";
import { PressReleaseReview } from "@/components/PressReleaseReview";
import { RichTextEditor } from "@/components/RichText";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { US_STATES } from "@/lib/states";
import { TOOLKITS, toolkitTitle } from "@/lib/toolkits";
import { usePageMeta } from "@/lib/usePageMeta";

export default function Admin() {
  usePageMeta({ title: "Admin Dashboard | Southern Black Girls Toolkits", description: "Staff dashboard." });
  const { user, loading: authLoading } = useAuth();
  const { toolkit } = useParams();

  if (authLoading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-sbg-raspberry" />
      </div>
    );

  if (!user) return <Login staff />;

  if (!user.is_staff)
    return <Gate title="Not authorized" body={`${user.email} is not a staff account.`} />;

  return toolkit ? <ToolkitDashboard toolkit={toolkit} /> : <AdminHub />;
}

function AdminHub() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({});
  const accessible = TOOLKITS.filter(
    (t) => user?.is_admin || user?.toolkit_scope === "all" ||
      (Array.isArray(user?.toolkit_scope) && user.toolkit_scope.includes(t.slug))
  );

  useEffect(() => {
    (async () => {
      const entries = await Promise.all(
        accessible.map(async (t) => {
          try {
            const { data } = await api.get(`/admin/stats?toolkit=${t.slug}`);
            return [t.slug, data];
          } catch {
            return [t.slug, null];
          }
        })
      );
      setCounts(Object.fromEntries(entries));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `accessible` is derived from user scope; load once per mount
  }, [user?.email]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />
      <div className="mx-auto w-full max-w-6xl px-5 py-10">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-pt-purple text-white">
            <LayoutGrid className="h-6 w-6" />
          </span>
          <div className="flex-1">
            <h1 className="font-display text-3xl font-black text-sbg-burgundy">Admin dashboard</h1>
            <p className="text-sm text-sbg-burgundy/60">Choose a toolkit to manage its participants and approved emails</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/account" data-testid="admin-account-link" className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15 transition-transform hover:-translate-y-0.5">
              <Bell className="h-4 w-4 text-sbg-fuchsia" /> My account
            </Link>
            <a href="/bgjcpt/create?sandbox=1" target="_blank" rel="noreferrer" data-testid="admin-sandbox-link" title="Walk through the frame-and-page steps as a new winner would. Nothing is saved or emailed." className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15 transition-transform hover:-translate-y-0.5">
              <FlaskConical className="h-4 w-4 text-pt-purple" /> Preview winner experience
            </a>
            {user?.is_admin && (
              <>
                <Link to="/admin/activity" data-testid="admin-activity-link" className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15 transition-transform hover:-translate-y-0.5">
                  <Activity className="h-4 w-4 text-sbg-fuchsia" /> Activity log
                </Link>
                <Link to="/admin/users" data-testid="admin-users-link" className="inline-flex items-center gap-1.5 rounded-full bg-pt-purple px-5 py-2.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5">
                  <UserCog className="h-4 w-4" /> Manage users
                </Link>
              </>
            )}
          </div>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {accessible.map((t) => {
            const c = counts[t.slug];
            const inner = (
              <div className={`relative h-full overflow-hidden rounded-3xl p-7 ring-1 transition-all ${t.live ? "bg-white ring-sbg-raspberry/10 hover:-translate-y-1 hover:shadow-xl" : "bg-white/60 ring-sbg-raspberry/5"}`}>
                <span className="block h-2 w-16 rounded-full" style={{ background: t.color }} />
                <div className="mt-4 flex items-center gap-2">
                  <h3 className="text-xl font-bold text-sbg-burgundy">{t.title}</h3>
                  {!t.live && <Lock className="h-4 w-4 text-sbg-burgundy/40" />}
                </div>
                {t.slug === "bgjcpt" && <p className="text-xs font-semibold text-pt-purple">with Pete &amp; Thomas Foundation</p>}
                <div className="mt-3 flex flex-wrap gap-5 text-sm text-sbg-burgundy/70">
                  <span><strong className="text-sbg-burgundy">{c ? c.total : 0}</strong> participants</span>
                  <span><strong className="text-amber-600">{c ? c.pending : 0}</strong> pending</span>
                </div>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-sbg-fuchsia">
                  Manage toolkit <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            );
            return (
              <Link key={t.slug} to={`/admin/${t.slug}`} data-testid={`admin-toolkit-card-${t.slug}`}>
                {inner}
              </Link>
            );
          })}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

function ToolkitDashboard({ toolkit }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [progressFilter, setProgressFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [frameFilter, setFrameFilter] = useState("");
  const [sort, setSort] = useState({ key: "created", dir: "desc" });
  const [search, setSearch] = useState("");
  const [frameView, setFrameView] = useState(null);
  const [page, setPage] = useState(1);
  const [toTrash, setToTrash] = useState(null);
  const [trashKey, setTrashKey] = useState(0);
  const PAGE_SIZE = 15;

  const trashItem = async () => {
    const w = toTrash; setToTrash(null);
    try {
      await api.delete(`/admin/items/${w.kind === "frame" ? "frame" : "page"}/${w.kind === "frame" ? w.id : w.slug}`);
      toast.success("Moved to Trash.");
      setTrashKey((k) => k + 1);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not move to Trash."); }
  };

  const load = useCallback(async () => {
    try {
      const [list, st] = await Promise.all([
        api.get(`/admin/winners?toolkit=${toolkit}`),
        api.get(`/admin/stats?toolkit=${toolkit}`),
      ]);
      setRows(list.data);
      setStats(st.data);
    } catch (e) {
      if (e.response?.status !== 403 && e.response?.status !== 401) toast.error("Could not load admin data.");
    } finally {
      setLoading(false);
    }
  }, [toolkit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, filter, progressFilter, stateFilter, frameFilter, sort]);

  const reviewWinner = async (slug, action) => {
    try {
      await api.post(`/admin/winners/${slug}/${action}`);
      toast.success(action === "approve" ? "Approved — their page is live and they've been emailed." : "Marked as rejected.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update that page.");
    }
  };

  const q = search.trim().toLowerCase();
  const FILTERS = {
    all: () => true,
    frame: (r) => !!r.framed_url || r.kind === "frame",
    published: (r) => r.kind !== "frame" && !!r.published,
    pending: (r) => r.status === "pending",
  };
  const progressOf = (r) => r.kind === "frame" ? "frame_only" : (r.status === "approved" || !r.status) ? (r.published ? "published" : "created") : r.status;
  const states = [...new Set(rows.map((r) => r.state).filter(Boolean))].sort();
  const frameIds = [...new Set(rows.map((r) => r.frame_id).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  const filtered = rows.filter((r) =>
    (FILTERS[filter] || FILTERS.all)(r) &&
    (!progressFilter || progressOf(r) === progressFilter) &&
    (!stateFilter || r.state === stateFilter) &&
    (!frameFilter || String(r.frame_id) === frameFilter) &&
    (!q || [r.name, r.email, r.city, r.state, r.project_title].filter(Boolean).join(" ").toLowerCase().includes(q))
  );
  const PROGRESS_ORDER = { pending: 0, unverified: 1, created: 2, published: 3, rejected: 4, frame_only: 5 };
  const sortVal = (r) => {
    switch (sort.key) {
      case "name": return (r.name || "").toLowerCase();
      case "progress": return PROGRESS_ORDER[progressOf(r)] ?? 9;
      case "updated": return r.updated_at || r.created_at || "";
      case "location": return `${r.state || ""} ${r.city || ""}`.trim().toLowerCase();
      case "email": return (r.email || "").toLowerCase();
      default: return r.created_at || "";
    }
  };
  const sorted = [...filtered].sort((a, b) => {
    const av = sortVal(a), bv = sortVal(b);
    const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
    return sort.dir === "asc" ? cmp : -cmp;
  });
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "created" || key === "updated" ? "desc" : "asc" }));
  const hasExtraFilters = progressFilter || stateFilter || frameFilter;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = sorted.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />
      <div className="mx-auto w-full max-w-6xl px-5 py-10">
        <Link to="/admin" data-testid="admin-back-link" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sbg-raspberry hover:underline">
          <ArrowLeft className="h-4 w-4" /> All toolkits
        </Link>
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-pt-purple text-white">
            <Shield className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-display text-3xl font-black text-sbg-burgundy" data-testid="admin-toolkit-title">{toolkitTitle(toolkit)}</h1>
            <p className="text-sm text-sbg-burgundy/60">{toolkit === "bgjcpt" ? "with Pete & Thomas Foundation · " : ""}Participants &amp; approvals for this toolkit</p>
          </div>
        </div>

        {stats && (
          <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard icon={Users} label="Participants" value={stats.total} active={filter === "all"} onClick={() => setFilter("all")} testId="stat-all" />
            <StatCard icon={ImageIcon} label="With a frame" value={stats.with_photo} active={filter === "frame"} onClick={() => setFilter(filter === "frame" ? "all" : "frame")} testId="stat-frame" />
            <StatCard icon={FileText} label="In gallery" value={stats.published} active={filter === "published"} onClick={() => setFilter(filter === "published" ? "all" : "published")} testId="stat-published" />
            <StatCard icon={Clock} label="Pending review" value={stats.pending ?? 0} active={filter === "pending"} onClick={() => setFilter(filter === "pending" ? "all" : "pending")} testId="stat-pending" highlight={(stats.pending ?? 0) > 0} />
          </div>
        )}
        {filter !== "all" && (
          <div className="mt-3 flex items-center gap-2 text-sm text-sbg-burgundy/70" data-testid="active-filter">
            Showing <strong className="text-sbg-burgundy">{filter === "frame" ? "entries with a frame" : filter === "published" ? "pages listed in the gallery" : "pages pending review"}</strong>
            <button data-testid="clear-filter-btn" onClick={() => setFilter("all")} className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-bold text-sbg-raspberry ring-1 ring-sbg-raspberry/20"><X className="h-3 w-3" /> Clear</button>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <Link to={`/admin/${toolkit}/approved`} data-testid="manage-approved-link" className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15 transition-transform hover:-translate-y-0.5">
            <CheckCircle2 className="h-4 w-4 text-sbg-fuchsia" /> Approved emails
          </Link>
          <Link to={`/admin/${toolkit}/content`} data-testid="manage-content-link" className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15 transition-transform hover:-translate-y-0.5">
            <FileText className="h-4 w-4 text-pt-purple" /> Page content &amp; frames
          </Link>
          {user?.is_admin && <PurgeTestData toolkit={toolkit} onDone={load} />}
          <ExportFramesButton toolkit={toolkit} count={stats?.with_photo} />
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-sbg-raspberry" /></div>
        ) : (
          <>
            <div className="mt-6 flex items-center gap-2 rounded-2xl bg-white px-4 py-2.5 ring-1 ring-sbg-raspberry/10">
              <Search className="h-4 w-4 text-sbg-burgundy/50" />
              <input data-testid="admin-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, project or location" className="w-full bg-transparent text-sm outline-none placeholder:text-sbg-burgundy/40" />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="admin-filter-bar">
              <Filter className="h-4 w-4 text-sbg-burgundy/50" />
              <select data-testid="filter-progress" value={progressFilter} onChange={(e) => setProgressFilter(e.target.value)} className={sel}>
                <option value="">All progress</option>
                <option value="pending">Pending</option>
                <option value="unverified">Email unverified</option>
                <option value="created">Page created</option>
                <option value="published">Published</option>
                <option value="rejected">Rejected</option>
                <option value="frame_only">Frame only</option>
              </select>
              <select data-testid="filter-state" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className={sel}>
                <option value="">All states</option>
                {states.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="filter-frame" value={frameFilter} onChange={(e) => setFrameFilter(e.target.value)} className={sel}>
                <option value="">All frames</option>
                {frameIds.map((f) => <option key={f} value={String(f)}>Frame {f}</option>)}
              </select>
              {hasExtraFilters && (
                <button data-testid="clear-extra-filters-btn" onClick={() => { setProgressFilter(""); setStateFilter(""); setFrameFilter(""); }} className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-bold text-sbg-raspberry ring-1 ring-sbg-raspberry/20"><X className="h-3 w-3" /> Reset filters</button>
              )}
              <span className="ml-auto text-xs text-sbg-burgundy/60" data-testid="result-count">{filtered.length} of {rows.length}</span>
            </div>
            {filtered.length === 0 ? (
              <p className="mt-10 text-center text-sbg-burgundy/60">No entries match.</p>
            ) : (
              <>
              <div className="mt-4 overflow-x-auto rounded-3xl bg-white ring-1 ring-sbg-raspberry/10">
                <table className="w-full text-left text-sm">
                  <thead className="bg-sbg-blush/50 text-xs uppercase tracking-wider text-sbg-raspberry">
                    <tr>
                      <th className="px-4 py-3">Frame</th>
                      <SortTh label="Name" k="name" sort={sort} onSort={toggleSort} />
                      <SortTh label="Progress" k="progress" sort={sort} onSort={toggleSort} />
                      <SortTh label="Created / Updated" k="created" altKey="updated" sort={sort} onSort={toggleSort} className="hidden lg:table-cell" />
                      <SortTh label="Email" k="email" sort={sort} onSort={toggleSort} className="hidden sm:table-cell" />
                      <SortTh label="Location" k="location" sort={sort} onSort={toggleSort} className="hidden md:table-cell" />
                      <th className="px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((w) => (
                      <tr key={w.id || w.slug} data-testid={`admin-row-${w.slug || w.id}`} className="border-t border-sbg-raspberry/10 hover:bg-sbg-blush/20">
                        <td className="px-4 py-3">
                          {w.framed_url ? (
                            <img src={w.framed_url} alt="" className="h-12 w-12 rounded-lg object-cover" />
                          ) : (
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-sbg-blush px-1 text-center text-[8px] font-bold leading-tight text-sbg-raspberry">{w.frame_id || "—"}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-semibold text-sbg-burgundy">{w.name || "—"}</td>
                        <td className="px-4 py-3"><ProgressBadge row={w} /></td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-sbg-burgundy/70">
                          <div>Created {fmtDate(w.created_at)}</div>
                          {w.updated_at && <div className="text-sbg-burgundy/50">Edited {fmtDate(w.updated_at)}</div>}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell text-sbg-burgundy/70">{w.email || "—"}</td>
                        <td className="px-4 py-3 hidden md:table-cell text-sbg-burgundy/70">{[w.city, w.state].filter(Boolean).join(", ") || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                          {w.kind === "frame" ? (
                            <button data-testid={`admin-frameview-${w.id}`} onClick={() => setFrameView(w)} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-sbg-burgundy/80 px-3 py-1.5 text-xs font-bold text-white"><Pencil className="h-3.5 w-3.5" /> View info</button>
                          ) : (
                            <button data-testid={`admin-view-${w.slug}`} onClick={() => setSelected(w.slug)} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-sbg-raspberry px-3 py-1.5 text-xs font-bold text-white"><Pencil className="h-3.5 w-3.5" /> View / edit</button>
                          )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between gap-3" data-testid="admin-pagination">
                  <span className="text-sm text-sbg-burgundy/60">
                    Showing {(pageSafe - 1) * PAGE_SIZE + 1}–{Math.min(pageSafe * PAGE_SIZE, filtered.length)} of {filtered.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button data-testid="admin-prev-page" disabled={pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="inline-flex items-center gap-1 rounded-full bg-white px-4 py-2 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15 disabled:opacity-40">
                      <ArrowLeft className="h-4 w-4" /> Prev
                    </button>
                    <span className="text-sm font-semibold text-sbg-burgundy">Page {pageSafe} / {totalPages}</span>
                    <button data-testid="admin-next-page" disabled={pageSafe >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="inline-flex items-center gap-1 rounded-full bg-white px-4 py-2 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15 disabled:opacity-40">
                      Next <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
              </>
            )}
          </>
        )}
        <TrashSection toolkit={toolkit} refreshKey={trashKey} onChange={load} />
      </div>

      <ConfirmDialog open={!!toTrash} onOpenChange={(o) => !o && setToTrash(null)} confirmLabel="Move to Trash" onConfirm={trashItem}
        title={`Move ${toTrash?.name || toTrash?.email || "this entry"} to Trash?`}
        description="It will be hidden from the dashboard and public pages. You can restore it from the Trash section; only an Admin can delete it permanently." />
      {selected && <DetailModal slug={selected} onClose={() => setSelected(null)} onSaved={load} onReview={reviewWinner} onTrash={(w) => { setSelected(null); setToTrash({ ...w, kind: "page" }); }} />}
      {frameView && <FrameInfoModal frame={frameView} onClose={() => setFrameView(null)} onTrash={(f) => { setFrameView(null); setToTrash({ ...f, kind: "frame" }); }} />}
      <SiteFooter />
    </div>
  );
}

function ExportFramesButton({ toolkit, count }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/frames-export", { params: { toolkit }, responseType: "blob", timeout: 300000 });
      const name = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || `${toolkit}-frames.zip`;
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Your frames ZIP is downloading.");
    } catch (e) {
      let msg = "Could not build the ZIP.";
      try { msg = JSON.parse(await e.response?.data?.text())?.detail || msg; } catch { /* ignore */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <button data-testid="export-frames-btn" onClick={run} disabled={busy || !count} title="Download every framed image for this toolkit as one ZIP (includes a manifest.csv)"
      className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2.5 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15 transition-transform hover:-translate-y-0.5 disabled:opacity-50">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {busy ? "Building ZIP…" : `Download all frames${count ? ` (${count})` : ""}`}
    </button>
  );
}

function PurgeTestData({ toolkit, onDone }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const ask = async () => {
    try {
      const { data } = await api.get(`/admin/test-data?toolkit=${toolkit}`);
      if (!data.winners.length && !data.frames) { toast.success("No test data found."); return; }
      setPreview(data); setOpen(true);
    } catch { toast.error("Could not scan for test data."); }
  };
  const purge = async () => {
    try {
      const { data } = await api.delete(`/admin/test-data?toolkit=${toolkit}`);
      toast.success(`Removed ${data.winners_removed} test pages and ${data.frames_removed} test frames.`);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove test data."); }
  };
  return (
    <>
      <button data-testid="purge-test-data-btn" onClick={ask} className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15 transition-transform hover:-translate-y-0.5">
        <Trash2 className="h-4 w-4 text-sbg-raspberry" /> Remove test data
      </button>
      <ConfirmDialog open={open} onOpenChange={setOpen} confirmLabel="Remove all" onConfirm={purge}
        title={`Remove ${preview?.winners.length || 0} test pages and ${preview?.frames || 0} test frames?`}
        description={`Deletes entries with @resend.dev / @example.com emails or QA/TEST names: ${(preview?.winners || []).slice(0, 8).map((w) => w.name || w.email).join(", ")}${(preview?.winners.length || 0) > 8 ? "…" : ""}. This cannot be undone.`} />
    </>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function ProgressBadge({ row }) {
  let cls = "bg-sbg-burgundy/10 text-sbg-burgundy/70", label = "Frame only";
  if (row.kind !== "frame") {
    const s = row.status || "approved";
    if (s === "rejected") { cls = "bg-sbg-burgundy/10 text-sbg-burgundy/70"; label = "Rejected"; }
    else if (s === "unverified") { cls = "bg-sbg-blush text-sbg-raspberry"; label = "Email unverified"; }
    else if (s === "pending") { cls = "bg-amber-100 text-amber-700"; label = "Pending"; }
    else if (row.published) { cls = "bg-emerald-100 text-emerald-700"; label = "Published"; }
    else { cls = "bg-sky-100 text-sky-700"; label = "Page created"; }
  }
  return (
    <span data-testid="progress-badge" className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

function FrameInfoModal({ frame, onClose, onTrash }) {
  const downloadRaw = async () => {
    try {
      const resp = await api.get(frame.raw_url, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `raw-photo-${frame.id}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download raw photo.");
    }
  };
  const bits = [
    ["Frame", frame.frame_id], ["Name", frame.name], ["Project", frame.project_title],
    ["What it does", frame.project_description], ["Who it serves", frame.project_serves],
    ["When", frame.project_when], ["Location", [frame.city, frame.state].filter(Boolean).join(", ")],
    ["Downloaded", fmtDate(frame.created_at)],
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="frame-info-modal" className="my-8 w-full max-w-lg rounded-3xl bg-background p-6 shadow-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-black text-sbg-burgundy">Frame download</h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-sbg-blush"><X className="h-5 w-5 text-sbg-burgundy" /></button>
        </div>
        <p className="mt-1 text-sm text-sbg-burgundy/60">This person downloaded a frame but hasn't created a page yet.</p>
        {frame.framed_url && (
          <div className="mt-4 text-center">
            <img src={frame.framed_url} alt="Downloaded frame" data-testid="frame-info-image" className="mx-auto w-56 rounded-2xl shadow ring-1 ring-sbg-raspberry/10" />
          </div>
        )}
        {frame.raw_url && (
          <div className="mt-3 text-center">
            <button onClick={downloadRaw} data-testid="frame-download-raw" className="inline-flex items-center gap-2 rounded-full bg-pt-purple px-4 py-2 text-xs font-bold text-white">
              <Download className="h-4 w-4" /> Download raw photo
            </button>
          </div>
        )}
        <div className="mt-4 space-y-2 rounded-2xl bg-white p-4 text-sm ring-1 ring-sbg-raspberry/10">
          {bits.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4">
              <span className="font-bold text-sbg-burgundy">{k}</span>
              <span className="text-right text-sbg-burgundy/70">{v || "—"}</span>
            </div>
          ))}
        </div>
        <div className="mt-5 flex justify-end">
          <button data-testid="frame-trash-btn" onClick={() => onTrash(frame)} className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/20 hover:bg-sbg-blush">
            <Trash2 className="h-3.5 w-3.5 text-sbg-raspberry" /> Move to Trash
          </button>
        </div>
      </div>
    </div>
  );
}

const sel = "rounded-full bg-white px-3 py-1.5 text-xs font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15 outline-none focus:ring-sbg-fuchsia";

function SortTh({ label, k, altKey, sort, onSort, className = "" }) {
  const active = sort.key === k || (altKey && sort.key === altKey);
  const next = () => onSort(altKey && sort.key === k && sort.dir === "asc" ? altKey : k);
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  const suffix = altKey && sort.key === altKey ? " (edited)" : altKey && sort.key === k ? " (created)" : "";
  return (
    <th className={`px-4 py-3 ${className}`}>
      <button type="button" data-testid={`sort-${k}`} onClick={next} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-sbg-fuchsia ${active ? "text-sbg-fuchsia" : ""}`}>
        {label}{suffix} <Icon className="h-3 w-3" />
      </button>
    </th>
  );
}

function StatCard({ icon: Icon, label, value, active, onClick, testId, highlight }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-pressed={active}
      className={`rounded-2xl p-5 text-left ring-1 transition-all hover:-translate-y-0.5 hover:shadow-md ${active ? "bg-sbg-burgundy text-white ring-sbg-burgundy" : "bg-white ring-sbg-raspberry/10"}`}>
      <Icon className={`h-5 w-5 ${active ? "text-sbg-blush" : highlight ? "text-amber-500" : "text-sbg-fuchsia"}`} />
      <div className={`mt-2 font-display text-3xl font-black ${active ? "text-white" : highlight ? "text-amber-600" : "text-sbg-burgundy"}`}>{value}</div>
      <div className={`text-xs ${active ? "text-white/70" : "text-sbg-burgundy/60"}`}>{label}{active ? " · filtering" : ""}</div>
    </button>
  );
}

function Gate({ title, body, children }) {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />
      <div className="mx-auto max-w-md px-5 py-24 text-center">
        <h1 className="font-display text-3xl font-black text-sbg-burgundy">{title}</h1>
        <p className="mt-3 text-sbg-burgundy/70">{body}</p>
        <div className="mt-6">{children}</div>
      </div>
      <SiteFooter />
    </div>
  );
}

const fld = "w-full rounded-xl border border-sbg-raspberry/20 bg-white px-3 py-2 text-sm outline-none focus:border-sbg-fuchsia";

function DetailModal({ slug, onClose, onSaved, onReview, onTrash }) {
  const [w, setW] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [viewingAs, setViewingAs] = useState(false);
  const viewAs = async () => {
    setViewingAs(true);
    const tab = window.open("", "_blank");
    try {
      const { data } = await api.post(`/admin/winners/${slug}/view-as`);
      if (tab) tab.location.href = data.url; else window.open(data.url, "_blank");
      toast.success("Opened a read-only preview as this winner (expires in 30 min).");
    } catch (e) { tab?.close(); toast.error(e.response?.data?.detail || "Could not open preview."); }
    finally { setViewingAs(false); }
  };

  const resendWelcome = async () => {
    setResending(true);
    try {
      const { data } = await api.post(`/admin/winners/${slug}/resend-welcome`);
      toast.success(`Welcome email sent to ${data.to}.`);
      const r = await api.get(`/admin/winners/${slug}`);
      setW((prev) => ({ ...prev, history: r.data.history }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email.");
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/admin/winners/${slug}`);
      setW(data);
    })();
  }, [slug]);

  const set = (k) => (e) =>
    setW((prev) => ({ ...prev, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/winners/${slug}`, {
        name: w.name, age: String(w.age ?? ""), show_name: w.show_name, project_title: w.project_title,
        project_description: w.project_description, project_serves: w.project_serves,
        project_when: w.project_when, city: w.city, state: w.state, quote: w.quote,
        instagram: w.instagram, media_contact_name: w.media_contact_name,
        media_contact_email: w.media_contact_email, media_contact_phone: w.media_contact_phone,
        published: w.published,
      });
      toast.success("Saved.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const downloadRaw = async () => {
    try {
      const resp = await api.get(w.raw_url, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `raw-photo-${slug}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download raw photo.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        data-testid="admin-detail-modal"
        className="my-8 w-full max-w-3xl rounded-3xl bg-background p-6 shadow-2xl sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl font-black text-sbg-burgundy">{w?.name || "Participant"}</h2>
          <button onClick={onClose} data-testid="admin-modal-close" className="rounded-full p-2 hover:bg-sbg-blush">
            <X className="h-5 w-5 text-sbg-burgundy" />
          </button>
        </div>

        {!w ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-sbg-raspberry" /></div>
        ) : (
          <div className="mt-5 space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl bg-white p-4 ring-1 ring-sbg-raspberry/10">
                <div className="text-xs font-bold uppercase tracking-wider text-sbg-raspberry">Framed photo</div>
                {w.framed_url ? (
                  <img src={w.framed_url} alt="" className="mt-2 w-full rounded-xl" />
                ) : (
                  <p className="mt-2 text-sm text-sbg-burgundy/50">No frame uploaded.</p>
                )}
              </div>
              <div className="rounded-2xl bg-white p-4 ring-1 ring-sbg-raspberry/10">
                <div className="text-xs font-bold uppercase tracking-wider text-pt-purple">Raw (unframed) photo</div>
                <p className="mt-2 text-sm text-sbg-burgundy/60">Original upload, admin-only.</p>
                {w.raw_url ? (
                  <button onClick={downloadRaw} data-testid="admin-download-raw" className="mt-3 inline-flex items-center gap-2 rounded-full bg-pt-purple px-4 py-2 text-xs font-bold text-white">
                    <Download className="h-4 w-4" /> Download raw photo
                  </button>
                ) : (
                  <p className="mt-2 text-sm text-sbg-burgundy/50">No raw photo.</p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={w.landing_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15">
                    <ExternalLink className="h-3.5 w-3.5" /> Page
                  </a>
                  <button onClick={viewAs} disabled={viewingAs} data-testid="admin-view-as" title="Open their page in a new tab exactly as they see it when signed in (read-only, 30 min)" className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-3 py-1.5 text-xs font-bold text-amber-950 disabled:opacity-60">
                    {viewingAs ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} View as this winner
                  </button>
                  <button onClick={resendWelcome} disabled={resending} data-testid="admin-resend-welcome" title={`Resend the welcome email (frame, page link, edit steps) to ${w.email}`} className="inline-flex items-center gap-1.5 rounded-full bg-sbg-turquoise px-3 py-1.5 text-xs font-bold text-sbg-burgundy disabled:opacity-60">
                    {resending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Resend welcome email
                  </button>
                </div>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name"><input className={fld} value={w.name || ""} onChange={set("name")} data-testid="admin-edit-name" /></Field>
              <Field label="Age"><input type="number" min="1" max="120" inputMode="numeric" className={fld} value={w.age || ""} onChange={set("age")} data-testid="admin-edit-age" /></Field>
              <Field label="Instagram"><input className={fld} value={w.instagram || ""} onChange={set("instagram")} /></Field>
              <Field label="Project title"><input className={fld} value={w.project_title || ""} onChange={set("project_title")} /></Field>
              <Field label="Who it serves"><input className={fld} value={w.project_serves || ""} onChange={set("project_serves")} /></Field>
              <Field label="City"><input className={fld} value={w.city || ""} onChange={set("city")} /></Field>
              <Field label="State">
                <select className={fld} value={w.state || ""} onChange={set("state")}>
                  <option value="">—</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Project description"><RichTextEditor testId="admin-description" value={w.project_description || ""} onChange={(html) => setW((p) => ({ ...p, project_description: html }))} minRows={3} /></Field>
            <Field label="Quote"><textarea rows={2} className={fld} value={w.quote || ""} onChange={set("quote")} /></Field>

            <div className="rounded-2xl border border-pt-lavender/40 bg-pt-lavender/10 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-pt-purple">Media contact</div>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <input className={fld} placeholder="Name" value={w.media_contact_name || ""} onChange={set("media_contact_name")} />
                <input className={fld} placeholder="Email" value={w.media_contact_email || ""} onChange={set("media_contact_email")} />
                <input className={fld} placeholder="Phone" value={w.media_contact_phone || ""} onChange={set("media_contact_phone")} />
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm font-bold text-sbg-burgundy">
              <input type="checkbox" checked={!!w.published} onChange={set("published")} className="h-5 w-5 accent-sbg-fuchsia" data-testid="admin-published-toggle" />
              <span>Show in Winner Gallery <span className="block text-xs font-normal text-sbg-burgundy/60">Lists this page in the public gallery (also requires the winner's gallery opt-in). The page itself stays live while approved.</span></span>
            </label>

            {w.status === "pending" && (
              <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-amber-50 p-4" data-testid="modal-review-actions">
                <span className="flex-1 text-sm font-semibold text-amber-800">This page is pending review.</span>
                <button data-testid="modal-approve-btn" onClick={() => { onReview(slug, "approve"); onClose(); }} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-5 py-2 text-sm font-bold text-white"><CheckCircle2 className="h-4 w-4" /> Approve</button>
                <button data-testid="modal-reject-btn" onClick={() => { onReview(slug, "reject"); onClose(); }} className="inline-flex items-center gap-1.5 rounded-full bg-sbg-burgundy/80 px-5 py-2 text-sm font-bold text-white"><Ban className="h-4 w-4" /> Reject</button>
              </div>
            )}

            <p className="text-xs text-sbg-burgundy/50" data-testid="modal-dates">
              Created {fmtDate(w.created_at)}{w.updated_at ? ` · Last edited ${fmtDate(w.updated_at)}` : ""}
            </p>
            <div className="flex items-center gap-2 text-xs text-sbg-burgundy/60">
              <span>Last sign-in:</span>
              <LoginInfo email={w.email} last={w.last_login} testId="winner-login-info" />
            </div>

            <ChangeHistory history={w.history} />

            <PressReleaseReview slug={slug} pageApproved={(w.status || "approved") === "approved"} email={w.email} onChanged={onSaved} />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <button data-testid="admin-trash-btn" onClick={() => onTrash(w)} className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2.5 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/20 hover:bg-sbg-blush">
                <Trash2 className="h-4 w-4 text-sbg-raspberry" /> Move to Trash
              </button>
              <div className="flex gap-3">
              <button onClick={onClose} className="rounded-full bg-sbg-blush px-5 py-2.5 text-sm font-bold text-sbg-raspberry">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="admin-save-btn" className="inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-6 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
              </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-bold text-sbg-burgundy">{label}</span>
      {children}
    </label>
  );
}
